import React, { useEffect, useState } from 'react';
import { getAllUsers, addCredits, setCredits, updateUserRole, updateUserProfile, getAppSettings, saveAppSettings } from '../utils/firestore';
import { FullLoader } from '../components/Loader';
import { isValidEmail } from '../utils/validate';
import { useAuth } from '../hooks/useAuth';

export default function Admin() {
  const { createUser, currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('users');
  const [appSettings, setAppSettings] = useState(null);
  const [topupModal, setTopupModal] = useState(null);
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(null); // {id, name, email, password}
  const [topupAmount, setTopupAmount] = useState(50);
  const [newUser, setNewUser] = useState({ name: '', email: '', password: '', role: 'user', credits: 100 });
  const [alert, setAlert] = useState(null);
  const [syncing, setSyncing] = useState(false);

  // Settings state
  const [fromNames, setFromNames] = useState([]);
  const [fromPrefixes, setFromPrefixes] = useState([]);
  const [replyTo, setReplyTo] = useState('');
  const [newFromName, setNewFromName] = useState('');
  const [newFromPrefix, setNewFromPrefix] = useState('');

  async function load() {
    const [data, settings] = await Promise.all([getAllUsers(), getAppSettings()]);
    setUsers(data);
    setAppSettings(settings);
    setFromNames(settings.fromNames || []);
    setFromPrefixes(settings.fromPrefixes || []);
    setReplyTo(settings.replyTo || 'cmfellowship-mah@gov.in');
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function showAlert(msg, type = 'success') {
    setAlert({ msg, type });
    setTimeout(() => setAlert(null), 5000);
  }

  async function handleTopup() {
    await addCredits(topupModal.id, topupAmount);
    setTopupModal(null);
    showAlert(`${topupAmount} credits added to ${topupModal.name}`);
    load();
  }

  async function handleSetCredits(uid, val) {
    await setCredits(uid, parseInt(val));
    load();
  }

  async function handleAddUser() {
    if (!newUser.name.trim()) { showAlert('Please enter a name', 'danger'); return; }
    if (!isValidEmail(newUser.email)) { showAlert('Please enter a valid email address', 'danger'); return; }
    if (newUser.password.length < 6) { showAlert('Password must be at least 6 characters', 'danger'); return; }
    try {
      await createUser(newUser.email, newUser.password, newUser.name, newUser.role, parseInt(newUser.credits));
      setAddModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'user', credits: 100 });
      showAlert(`User ${newUser.name} created`);
      load();
    } catch (err) { showAlert(err.message, 'danger'); }
  }

  async function handleEditUser() {
    if (!editModal.name.trim()) { showAlert('Name is required', 'danger'); return; }
    if (!isValidEmail(editModal.email)) { showAlert('Valid email is required', 'danger'); return; }
    if (editModal.password && editModal.password.length < 6) { showAlert('Password must be at least 6 characters', 'danger'); return; }
    try {
      const payload = { uid: editModal.id, name: editModal.name, email: editModal.email };
      if (editModal.password) payload.password = editModal.password;
      const res = await fetch('/api/update-user', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      setEditModal(null);
      showAlert(`User ${editModal.name} updated successfully`);
      load();
    } catch (err) { showAlert(err.message, 'danger'); }
  }

  async function handleRoleChange(uid, role) {
    await updateUserRole(uid, role);
    // If admin, set unlimitedCredits true by default
    if (role === 'admin') await updateUserProfile(uid, { unlimitedCredits: true });
    load();
  }

  async function toggleUnlimited(uid, current) {
    await updateUserProfile(uid, { unlimitedCredits: !current });
    load();
  }

  async function saveSettings() {
    await saveAppSettings({ fromNames, fromPrefixes, replyTo });
    showAlert('Settings saved');
  }

  const totalIssued = users.reduce((s, u) => s + (u.credits || 0) + (u.creditsUsed || 0), 0);
  const totalRemaining = users.reduce((s, u) => s + (u.credits || 0), 0);
  const totalUsed = users.reduce((s, u) => s + (u.creditsUsed || 0), 0);

  return (
    <>
      {(loading || syncing) && <FullLoader message={syncing ? 'Saving…' : 'Loading users…'} />}
      <div className="topbar">
        <div>
          <div className="topbar-title">Admin panel</div>
          <div className="topbar-sub">Manage users, credits and app configuration</div>
        </div>
        {tab === 'users' && <button className="btn btn-primary" onClick={() => setAddModal(true)}>+ Add user</button>}
        {tab === 'settings' && <button className="btn btn-primary" onClick={saveSettings}>Save settings</button>}
      </div>

      <div style={{ padding: '0 24px', borderBottom: '0.5px solid var(--border)', background: 'var(--bg)', display: 'flex', gap: 2 }}>
        {[['users', 'Users & Credits'], ['masters', 'Email masters'], ['quota', 'Quota overview']].map(([k, v]) => (
          <div key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{v}</div>
        ))}
      </div>

      <div className="content">
        {alert && <div className={`alert alert-${alert.type}`}>{alert.msg}</div>}

        {/* Users tab */}
        {tab === 'users' && (
          <div className="card">
            <div className="card-title">Users & credit management</div>
            {loading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Loading…</p> : (
              <table className="data-table">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Unlimited</th><th>Credits</th><th>Used</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td style={{ fontWeight: 500 }}>{u.name}</td>
                      <td style={{ color: 'var(--text3)', fontSize: 12 }}>{u.email}</td>
                      <td>
                        <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} style={{ width: 'auto', padding: '3px 8px', fontSize: 12 }}>
                          <option value="user">User</option>
                          <option value="admin">Admin</option>
                        </select>
                      </td>
                      <td>
                        <input type="checkbox" checked={!!u.unlimitedCredits} onChange={() => toggleUnlimited(u.id, !!u.unlimitedCredits)} />
                      </td>
                      <td>
                        {u.unlimitedCredits ? <span className="badge badge-info">∞</span> : <strong>{u.credits || 0}</strong>}
                      </td>
                      <td style={{ color: 'var(--text3)' }}>{u.creditsUsed || 0}</td>
                      <td style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-sm" onClick={() => setEditModal({ id: u.id, name: u.name, email: u.email, password: '' })}>✏ Edit</button>
                        {!u.unlimitedCredits && <button className="btn btn-sm" onClick={() => setTopupModal(u)}>+ Credits</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Masters tab */}
        {tab === 'masters' && (
          <>
            <div className="card">
              <div className="card-title">Reply-to address</div>
              <div className="field">
                <label className="field-label">Reply-to email (locked for users)</label>
                <input type="email" value={replyTo} onChange={e => setReplyTo(e.target.value)} />
              </div>
            </div>
            <div className="card">
              <div className="card-title">From names master list</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {fromNames.map((n, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="text" value={n} onChange={e => { const a = [...fromNames]; a[i] = e.target.value; setFromNames(a); }} style={{ flex: 1 }} />
                    <button className="btn btn-sm btn-danger" onClick={() => setFromNames(fromNames.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" value={newFromName} onChange={e => setNewFromName(e.target.value)} placeholder="e.g. CM Fellowship Maharashtra" style={{ flex: 1 }} />
                <button className="btn btn-sm" onClick={() => { if (newFromName.trim()) { setFromNames([...fromNames, newFromName.trim()]); setNewFromName(''); } }}>Add</button>
              </div>
            </div>
            <div className="card">
              <div className="card-title">From email prefixes master list</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 12 }}>These become the dropdown options for users. Domain @cmfellowship.in is fixed.</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {fromPrefixes.map((p, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input type="text" value={p} onChange={e => { const a = [...fromPrefixes]; a[i] = e.target.value; setFromPrefixes(a); }} style={{ flex: 1 }} />
                    <span style={{ color: 'var(--text3)', fontSize: 13 }}>@cmfellowship.in</span>
                    <button className="btn btn-sm btn-danger" onClick={() => setFromPrefixes(fromPrefixes.filter((_, j) => j !== i))}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="text" value={newFromPrefix} onChange={e => setNewFromPrefix(e.target.value.replace(/@.*/, ''))} placeholder="e.g. CMFellowship-News" style={{ flex: 1 }} />
                <span style={{ color: 'var(--text3)', fontSize: 13 }}>@cmfellowship.in</span>
                <button className="btn btn-sm" onClick={() => { if (newFromPrefix.trim()) { setFromPrefixes([...fromPrefixes, newFromPrefix.trim()]); setNewFromPrefix(''); } }}>Add</button>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-primary" onClick={saveSettings}>Save all masters</button>
            </div>
          </>
        )}

        {/* Quota tab */}
        {tab === 'quota' && (
          <>
            <div className="stat-grid">
              <div className="stat-card"><div className="stat-label">Total users</div><div className="stat-val">{users.length}</div></div>
              <div className="stat-card"><div className="stat-label">Total credits issued</div><div className="stat-val">{totalIssued}</div></div>
              <div className="stat-card"><div className="stat-label">Total credits used</div><div className="stat-val">{totalUsed}</div></div>
            </div>
            <div className="stat-grid">
              <div className="stat-card"><div className="stat-label">Credits remaining</div><div className="stat-val">{totalRemaining}</div></div>
              <div className="stat-card"><div className="stat-label">Unlimited admins</div><div className="stat-val">{users.filter(u => u.unlimitedCredits).length}</div></div>
              <div className="stat-card"><div className="stat-label">Usage rate</div><div className="stat-val">{totalIssued > 0 ? Math.round((totalUsed / totalIssued) * 100) : 0}%</div></div>
            </div>
            <div className="card">
              <div className="card-title">Per-user quota</div>
              <table className="data-table">
                <thead><tr><th>User</th><th>Type</th><th>Allocated</th><th>Used</th><th>Remaining</th><th>Usage</th></tr></thead>
                <tbody>
                  {users.map(u => {
                    const pct = (u.credits + u.creditsUsed) > 0 ? Math.round(((u.creditsUsed || 0) / ((u.credits || 0) + (u.creditsUsed || 0))) * 100) : 0;
                    return (
                      <tr key={u.id}>
                        <td style={{ fontWeight: 500 }}>{u.name}</td>
                        <td>{u.unlimitedCredits ? <span className="badge badge-info">Unlimited</span> : <span className="badge badge-success">Limited</span>}</td>
                        <td>{u.unlimitedCredits ? '∞' : (u.credits || 0) + (u.creditsUsed || 0)}</td>
                        <td>{u.creditsUsed || 0}</td>
                        <td>{u.unlimitedCredits ? '∞' : u.credits || 0}</td>
                        <td>
                          {!u.unlimitedCredits && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ height: 6, width: 80, background: 'var(--bg3)', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${pct}%`, background: pct > 80 ? 'var(--danger)' : 'var(--accent)', borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 11, color: 'var(--text3)' }}>{pct}%</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Topup modal */}
      {topupModal && (
        <div className="modal-overlay" onClick={() => setTopupModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add credits</div>
            <div className="modal-sub">Adding credits for <strong>{topupModal.name}</strong> (currently {topupModal.credits})</div>
            <div className="field">
              <label className="field-label">Credits to add</label>
              <input type="number" min="1" value={topupAmount} onChange={e => setTopupAmount(parseInt(e.target.value))} />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setTopupModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleTopup}>Add credits</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit user modal */}
      {editModal && (
        <div className="modal-overlay" onClick={() => setEditModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Edit user</div>
            <div className="modal-sub">Update name, email or password for this user</div>
            <div className="field">
              <label className="field-label">Full name</label>
              <input type="text" value={editModal.name} onChange={e => setEditModal({ ...editModal, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="field">
              <label className="field-label">Email address</label>
              <input type="email" value={editModal.email} onChange={e => setEditModal({ ...editModal, email: e.target.value })} placeholder="email@example.com" />
            </div>
            <div className="field">
              <label className="field-label">New password <span style={{ fontWeight: 400, color: 'var(--text3)' }}>(leave blank to keep current)</span></label>
              <input type="password" value={editModal.password} onChange={e => setEditModal({ ...editModal, password: e.target.value })} placeholder="••••••••" />
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setEditModal(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleEditUser}>Save changes</button>
            </div>
          </div>
        </div>
      )}

      {/* Add user modal */}
      {addModal && (
        <div className="modal-overlay" onClick={() => setAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add new user</div>
            <div className="modal-sub">Create a user account for a CM Fellowship member</div>
            <div className="field"><label className="field-label">Full name</label><input type="text" value={newUser.name} onChange={e => setNewUser({ ...newUser, name: e.target.value })} placeholder="Priya Sharma" /></div>
            <div className="field"><label className="field-label">Email</label><input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value })} placeholder="priya@cmfellowship.in" /></div>
            <div className="field"><label className="field-label">Password</label><input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="••••••••" /></div>
            <div className="row">
              <div className="col field">
                <label className="field-label">Role</label>
                <select value={newUser.role} onChange={e => setNewUser({ ...newUser, role: e.target.value })}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="col field">
                <label className="field-label">Starting credits</label>
                <input type="number" min="0" value={newUser.credits} onChange={e => setNewUser({ ...newUser, credits: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn" onClick={() => setAddModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddUser}>Create user</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
